import { buildTimeComparison, toPaylocityJob } from './timeComparison';
import moment from 'moment';
import { filterJobsForSchedule } from '../components/JobSchedulePage/utils/globalSearchUtils';

export const projectValue = (project, key) => project[key] ?? project[key === 'ID' ? 'id' : key[0].toLowerCase() + key.slice(1)];
const marked = value => value != null && String(value).trim() !== '';
export const engineeringGroup = project => {
  const number = String(projectValue(project, 'JobNumber') || '').trim();
  if (!/^\d{7}(?:-\d{2})?$/.test(number)) return null;
  if (marked(projectValue(project, 'ActualShipDate')) || ['JobStatus', 'ProjectStatus'].some(key => String(projectValue(project, key) || '').trim().toLowerCase() === 'shipped')) return 'shipped';
  if (['MechanicalDue', 'MechanicalDone', 'ElectricalDue', 'ElectricalDone'].every(key => marked(projectValue(project, key)))) return 'complete';
  if (marked(projectValue(project, 'EngineeringDone'))) return null;
  if (!['Mechanical', 'Electrical'].some(department => marked(projectValue(project, `${department}Due`)) && !marked(projectValue(project, `${department}Done`)))) return null;
  const normalized = { ...project };
  ['HideFromList', 'JobStatus', 'ProjectStatus'].forEach(key => { normalized[key] = projectValue(project, key); });
  return filterJobsForSchedule([normalized], '').length ? 'progress' : null;
};

// Match the schedule's chronological shipping-week order and job-number ties.
export const compareEngineeringShipDates = (left, right) => {
  const shipTime = project => {
    const value = projectValue(project, 'CurrentPlanShipDate');
    const time = marked(value) ? new Date(value).getTime() : NaN;
    return Number.isFinite(time) ? time : Infinity;
  };
  const leftTime = shipTime(left);
  const rightTime = shipTime(right);
  if (leftTime !== rightTime) return leftTime < rightTime ? -1 : 1;
  return String(projectValue(left, 'JobNumber') || '').localeCompare(String(projectValue(right, 'JobNumber') || ''));
};

export const groupEngineeringShipWeeks = (rows, today = moment()) => {
  const weeks = new Map();
  const currentWeek = moment(today).startOf('week');
  [...rows].sort((a, b) => compareEngineeringShipDates(a.project, b.project)).forEach(row => {
    const value = projectValue(row.project, 'CurrentPlanShipDate');
    const date = marked(value) ? moment(value, moment.ISO_8601) : null;
    const start = date?.isValid() ? date.clone().startOf('week') : null;
    const pastDue = start && start.isBefore(currentWeek);
    const key = pastDue ? 'past-due' : start ? start.format('YYYY-MM-DD') : 'unscheduled';
    if (!weeks.has(key)) weeks.set(key, {
      key,
      label: pastDue ? 'Past Due' : start
        ? `Week ${start.diff(currentWeek, 'weeks') + 1} (${start.isoWeek()}): ${start.format('MM/DD')} - ${start.clone().endOf('week').format('MM/DD')}`
        : 'Unscheduled',
      rows: [],
    });
    weeks.get(key).rows.push(row);
  });
  return [...weeks.values()].sort((a, b) => a.key === b.key ? 0 : a.key === 'past-due' || b.key === 'unscheduled' ? -1 : b.key === 'past-due' || a.key === 'unscheduled' ? 1 : a.key.localeCompare(b.key));
};

export const summarizeEngineeringJob = (project, records) => {
  let code;
  try { code = toPaylocityJob(projectValue(project, 'JobNumber')); } catch { return null; }
  const rows = buildTimeComparison(project, records, code);
  const engineering = rows.filter(row => ['MECHANICAL ENGINEERING', 'ELECTRICAL ENGINEERING'].includes(row.department));
  const allocations = engineering.map(row => row.allocated);
  const known = allocations.filter(value => value != null);
  const comparable = engineering.filter(row => row.hasTime && row.allocated != null);
  const departments = [
    { name: 'Mechanical', allocated: engineering[0].allocated, actual: engineering[0].hasTime ? engineering[0].actual : null },
    { name: 'Electrical', allocated: engineering[1].allocated, actual: engineering[1].hasTime ? engineering[1].actual : null },
  ].map(department => ({ ...department, due: projectValue(project, `${department.name}Due`), done: projectValue(project, `${department.name}Done`) }));
  return {
    departments,
    allocated: known.length ? known.reduce((sum, value) => sum + value, 0) : null,
    partial: known.length !== allocations.length,
    engineering: engineering.some(row => row.hasTime) ? engineering.reduce((sum, row) => sum + row.actual, 0) : null,
    actual: rows.some(row => row.hasTime) ? rows.reduce((sum, row) => sum + row.actual, 0) : null,
    variance: comparable.length ? comparable.reduce((sum, row) => sum + row.variance, 0) : null,
  };
};
